import { createRoot } from 'react-dom/client'
import Cookies from 'js-cookie'
import { BREAKPOINT_SMALLEST_CHROME_WINDOW } from './constants/breakpoints.ts'
import { components as accountMenuComponents } from './features/account_menu/index.js'
const { AccountMenu } = accountMenuComponents

const EXPANDED_CLASS_NAME = 'app-ui__layout-wrapper--sidebar-expanded'
const COLLAPSED_CLASS_NAME = 'app-ui__layout-wrapper--sidebar-collapsed'
const LARGE_SCREEN_EXPANDED_CLASS_NAME = 'app-ui__layout-wrapper--large-screen-expanded'
const LARGE_SCREEN_COLLAPSED_CLASS_NAME = 'app-ui__layout-wrapper--large-screen-collapsed'

window.Timetoast.appUI = {
  ...window.Timetoast.appUI,

  init: function () {
    window.Timetoast.appUI.initHeader()
    window.Timetoast.appUI.initSidePanel()
    window.Timetoast.shared.navigations.init()
  },

  initHeader: function () {
    const accountEl = document.querySelector('#js-account-nav-item')
    if (accountEl) {
      const root = createRoot(accountEl)
      root.render(<AccountMenu />)
    }
  },

  initSidePanel: function () {
    const layoutWrapper = document.querySelector('.js-app-ui__layout-wrapper')
    const hamburgerButton = document.querySelector('.js-burger-button')
    const largeScreenExpandButton = document.querySelector('.js-large-screen-sidebar-expand-button')
    const sidebar = document.querySelector('.js-side-panel')
    const collapseButton = document.querySelector('.js-side-panel-collapse-button')

    const isLargeScreen = () => window.innerWidth > BREAKPOINT_SMALLEST_CHROME_WINDOW

    // Check if sidebar is expanded, taking screen size into account
    const isSidebarExpanded = () => {
      if (!layoutWrapper) return false

      // On large screens, check large screen class
      if (isLargeScreen()) {
        return layoutWrapper.classList.contains(LARGE_SCREEN_EXPANDED_CLASS_NAME)
      }
      // On small screens, check regular expanded class
      else {
        return layoutWrapper.classList.contains(EXPANDED_CLASS_NAME)
      }
    }

    const expand = () => {
      if (layoutWrapper) {
        // Always add these classes
        layoutWrapper.classList.add(EXPANDED_CLASS_NAME)

        // On large screens, also add large screen class
        if (isLargeScreen()) {
          layoutWrapper.classList.add(LARGE_SCREEN_EXPANDED_CLASS_NAME)
          layoutWrapper.classList.remove(LARGE_SCREEN_COLLAPSED_CLASS_NAME)
        }

        layoutWrapper.classList.remove(COLLAPSED_CLASS_NAME)
      }
      setAriaExpanded(true)
      setSidebarAriaHidden(false)
      rememberState()
    }

    const collapse = () => {
      if (layoutWrapper) {
        layoutWrapper.classList.add(COLLAPSED_CLASS_NAME)

        // On large screens, also add large screen class
        if (isLargeScreen()) {
          layoutWrapper.classList.add(LARGE_SCREEN_COLLAPSED_CLASS_NAME)
          layoutWrapper.classList.remove(LARGE_SCREEN_EXPANDED_CLASS_NAME)
        }

        layoutWrapper.classList.remove(EXPANDED_CLASS_NAME)
      }
      setAriaExpanded(false)
      setSidebarAriaHidden(true)
      rememberState()
    }

    const toggle = () => (isSidebarExpanded() ? collapse() : expand())

    const rememberState = () => {
      // Only remember state for large screens
      if (isLargeScreen()) {
        if (layoutWrapper) {
          try {
            const state = isSidebarExpanded() ? 'expanded' : 'collapsed'
            Cookies.set('largeScreenSidebarState', state)
          } catch (error) {
            console.error('Failed to save sidebar state', error)
          }
        }
      }
    }

    // Utility helper to update aria-expanded attribute
    const setAria = (element, isExpanded) => {
      if (element) {
        element.setAttribute('aria-expanded', isExpanded.toString())
      }
    }

    const setAriaExpanded = isExpanded => {
      // Use the helper for all relevant elements
      setAria(largeScreenExpandButton, isExpanded)
      setAria(hamburgerButton, isExpanded)
      setAria(collapseButton, isExpanded)
      setAria(sidebar, isExpanded)
    }

    const bindEventListener = (element, event, callback) => {
      if (element) {
        element.addEventListener(event, callback)
        element.disabled = false
        element.removeAttribute('aria-disabled')
      }
    }

    const setSidebarAriaHidden = isHidden => {
      if (sidebar) {
        if (isHidden) {
          // If a descendant has focus, blur it and then shift focus to the hamburger button
          if (sidebar.contains(document.activeElement)) {
            document.activeElement.blur()
            if (hamburgerButton) {
              hamburgerButton.focus()
            }
          }
          sidebar.setAttribute('inert', '')
          sidebar.setAttribute('aria-hidden', 'true')
        } else {
          sidebar.removeAttribute('inert')
          sidebar.setAttribute('aria-hidden', 'false')
        }
      }
    }

    // Function to handle mobile initial state for the sidepanel
    const initMobileSidebarState = () => {
      if (window.innerWidth <= BREAKPOINT_SMALLEST_CHROME_WINDOW) {
        if (layoutWrapper) {
          layoutWrapper.classList.add(COLLAPSED_CLASS_NAME)
          layoutWrapper.classList.remove(EXPANDED_CLASS_NAME)
        }
      }
    }

    // enable sidebar button
    bindEventListener(largeScreenExpandButton, 'click', expand)

    // enable hamburger button
    bindEventListener(hamburgerButton, 'click', toggle)

    // enable collapse button (in side bar)
    bindEventListener(collapseButton, 'click', collapse)

    // Call mobile state initializer
    initMobileSidebarState()

    // Update aria attributes based on screen size and current state
    const updateAriaAttributes = () => {
      const expanded = isSidebarExpanded()
      setAriaExpanded(expanded)
      setSidebarAriaHidden(!expanded)
    }

    // Set initial aria states
    updateAriaAttributes()

    // Also update aria attributes when screen is resized
    window.addEventListener('resize', updateAriaAttributes)
  },
}
